import React, { useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Paper, Container } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";

import ForgotPasswordForm from "./ForgotPasswordForm";

export default function ForgotPasswordView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isPasswordReset, setIsPasswordReset] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);

  const handleSignIn = () => {
    navigate("/login");
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          padding: "40px 0",
        }}
      >
        {/* Success Message */}
        {isPasswordReset && (
          <Paper
            elevation={3}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 3,
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                margin: "0 auto 16px",
                textAlign: "center",
              }}
            >
              <img
                src="/logo.png"
                alt="Podcast Assistant Logo"
                style={{
                  height: "80px",
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}
            >
              Reset Link Sent!
            </Typography>
            <Typography
              component="p"
              sx={{ marginY: 2, textAlign: "center", color: "text.secondary" }}
            >
              We have sent you an email to{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {email}
              </Typography>
              . Please check your email to reset your password.
            </Typography>
            <Typography component="p" sx={{ marginY: 2, textAlign: "center" }}>
              Back to{" "}
              <Link
                href="/login"
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Paper>
        )}

        {/* Forgot Password Form */}
        {!isPasswordReset && (
          <Paper
            elevation={3}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 3,
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Forgot Password Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              {/* Logo instead of Avatar for forgot password form */}
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{
                    height: "80px",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                component="h1"
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Forgot Password
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, textAlign: "center" }}
              >
                Enter your email address and we'll send you a link to reset your
                password
              </Typography>
            </Box>

            {/* Forgot Password Form */}
            <ForgotPasswordForm
              email={searchParams.get("email") || ""}
              handlePasswordReset={(userEmail) => {
                setEmail(userEmail);
                setIsPasswordReset(true);
              }}
            />
          </Paper>
        )}

        {/* Footer Links */}
        {!isPasswordReset && (
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{" "}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={handleSignIn}
              >
                Sign in
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}
